import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../../utils/apiClient';

const FabricationLocationsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLocation, setNewLocation] = useState('');
  const [editingLocation, setEditingLocation] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch locations on modal open
  useEffect(() => {
    if (isOpen) {
      fetchLocations();
    }
  }, [isOpen]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError('');

      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      const locationsResponse = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/fabrication-locations'
      );

      if (locationsResponse.data.success) {
        setLocations(locationsResponse.data.data);
      } else {
        setError('Failed to fetch fabrication locations');
      }
    } catch (error) {
      console.error('Error fetching fabrication locations:', error);
      setError('Failed to fetch fabrication locations');
    } finally {
      setLoading(false);
    }
  };

  const addLocation = async () => {
    try {
      setError('');
      setSuccess('');

      if (!newLocation.trim()) {
        setError('Location name is required');
        return;
      }

      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      const addResponse = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/fabrication-locations',
        { location_name: newLocation.trim() }
      );

      if (addResponse.data.success) {
        setNewLocation('');
        setSuccess('Location added successfully');
        fetchLocations(); // Refresh the list
      } else {
        setError(addResponse.data.message || 'Failed to add location');
      }
    } catch (error) {
      console.error('Error adding fabrication location:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to add fabrication location');
      }
    }
  };

  const startEditing = (location) => {
    setEditingLocation(location.id);
    setEditValue(location.location_name);
    setError('');
    setSuccess('');
  };

  const cancelEditing = () => {
    setEditingLocation(null);
    setEditValue('');
    setError('');
    setSuccess('');
  };

  const updateLocation = async (id) => {
    try {
      setError('');
      setSuccess('');

      if (!editValue.trim()) {
        setError('Location name is required');
        return;
      }

      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      const updateResponse = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/fabrication-locations/${id}`,
        { location_name: editValue.trim() }
      );

      if (updateResponse.data.success) {
        setEditingLocation(null);
        setEditValue('');
        setSuccess('Location updated successfully');
        fetchLocations(); // Refresh the list
      } else {
        setError(updateResponse.data.message || 'Failed to update location');
      }
    } catch (error) {
      console.error('Error updating fabrication location:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to update fabrication location');
      }
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fabrication location?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');

      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      const deleteResponse = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/fabrication-locations/${id}`
      );

      if (deleteResponse.data.success) {
        setSuccess('Location deleted successfully');
        fetchLocations(); // Refresh the list
      } else {
        setError(deleteResponse.data.message || 'Failed to delete location');
      }
    } catch (error) {
      console.error('Error deleting fabrication location:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to delete fabrication location');
      }
    }
  };

  const handleKeyPress = (e, action, id = null) => {
    if (e.key === 'Enter') {
      if (action === 'add') {
        addLocation();
      } else if (action === 'update') {
        updateLocation(id);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '1rem'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>Edit Fabrication Locations</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666',
              padding: '0.5rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {/* Add New Location */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <input
            type="text"
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, 'add')}
            placeholder="Enter new fabrication location..."
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          <button
            onClick={addLocation}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: loading ? 0.6 : 1
            }}
          >
            Add
          </button>
        </div>

        {/* Locations List */}
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Current Locations</h3>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '1.5rem' }}>⏳</div>
              <p>Loading locations...</p>
            </div>
          ) : locations.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏭</div>
              <p style={{ color: '#6c757d', margin: 0 }}>No fabrication locations found</p>
            </div>
          ) : (
            <div style={{
              maxHeight: '300px',
              overflow: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}>
              {locations.map((location) => (
                <div
                  key={location.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: 'white'
                  }}
                >
                  {editingLocation === location.id ? (
                    // Edit mode
                    <div style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, 'update', location.id)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #007bff',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => updateLocation(location.id)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    // View mode
                    <>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {location.location_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          Created by: {location.created_by_name || 'Unknown'} | 
                          Created: {new Date(location.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          onClick={() => startEditing(location)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#ffc107',
                            color: '#212529',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteLocation(location.id)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '1rem',
          borderTop: '1px solid #e9ecef',
          paddingTop: '1rem'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FabricationLocationsModal;
